/**
 * Hub **hover hints**: portaled floating panels (not the browser `title` attribute). Product vocabulary — see
 * `docs/ui-style-guide.md` § **Hover hints (terminology)**.
 *
 * - **Hover hint with glyph (?):** **`HoverHintWithGlyph`**. **`PageTitleWithHelp`** / **`CardTitleWithHelp`** compose it next to headings.
 * - **Hover hint without glyph:**
 *   - **`HoverHintTrigger`** — anchor is the **`trigger`** you pass (explicit control).
 *   - **`HoverHintContent`** — anchor is the **`children`** you wrap (hit target is content; **`hoverHint`** is the portal body, usually a string, sometimes rich **`ReactNode`**).
 *
 * **`ReadOnlyHoverLayer`:** same panel style for explanatory hover over **disabled** controls.
 */
import type { CSSProperties, PropsWithChildren, ReactNode, RefObject } from "react";
import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  HOVER_HINT_APPROACH_IDLE_MS,
  HOVER_HINT_PANEL_SELECTOR,
  HOVER_HINT_ROOT_ATTR,
  hoverHintPointerAim,
  isNodeInsideForeignHoverHint,
  isNodeInsideHoverHintAnchor,
  isNodeInsideHoverHintSurface,
  isNodeInsideThisHintTree,
  type HoverHintPoint,
  type HoverHintRect,
} from "@/lib/hoverHintSurface";
import {
  mergeMeasuredPortaledHoverHintStyle,
  resolvePortaledHoverHintStyle,
} from "@/lib/portaledHoverHintStyle";
import { shouldIgnoreOpeningPointer } from "@/lib/touchOpenClose";
import { computeHoverHintFixedStyle, resolveHoverHintPortalZIndex } from "@/lib/viewportAnchoredPopover";

import { CardTitle } from "./Card";
import { HubBreadcrumbTitleRow } from "@/components/HubPageTitle";
import { resolvePageBreadcrumbLabel } from "@/lib/pageBreadcrumbLabel";
import { useNavigationTrail, usePageBreadcrumb } from "@/lib/navigationTrailContext";

/** Matches layout of `WorkOrdersPage` / `Dashboard` — no hover tint so the icon stays visually stable. */
const helpButtonClass =
  "inline-flex shrink-0 rounded p-0.5 text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1";

const helpIconClass: Record<"sm" | "md", string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
};

const portalHoverHintBodyClass =
  "pointer-events-auto w-max max-w-full overflow-visible whitespace-normal rounded-md border border-slate-200 bg-white px-2.5 py-2 text-left text-xs font-normal leading-snug text-slate-700 shadow-lg";

/** Inner body: shrink-wrap to copy. `block min-w-0` fills the 36rem panel cap. */
function portaledHoverHintInnerClass(content: ReactNode): string {
  const wrapLongCopy = typeof content === "string" || typeof content === "number";
  return wrapLongCopy ? "inline-block w-max max-w-full break-words" : "inline-block w-max max-w-full";
}

function portaledHoverHintPanelStyle(
  panelStyle: NonNullable<ReturnType<typeof computeHoverHintFixedStyle>>,
  portalZIndex: number,
): CSSProperties {
  return {
    position: "fixed",
    top: panelStyle.top,
    left: panelStyle.left,
    maxWidth: panelStyle.maxWidth,
    ...(panelStyle.maxHeight != null ? { maxHeight: panelStyle.maxHeight } : {}),
    transform: panelStyle.transform,
    zIndex: portalZIndex,
  };
}

function usePortaledHoverHintStyle(
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  open: boolean,
  align: "start" | "center" | "end",
  anchorRevision = 0,
): ReturnType<typeof computeHoverHintFixedStyle> | null {
  type HintStyle = ReturnType<typeof computeHoverHintFixedStyle>;
  const [positioned, setPositioned] = useState<{ revision: number; style: HintStyle } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPositioned(null);
    }
  }, [open]);

  const provisionalStyle =
    open && anchorRef.current
      ? computeHoverHintFixedStyle(anchorRef.current.getBoundingClientRect(), align, { contentHeight: null })
      : null;

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }
    const panel = panelRef.current;
    const contentHeight =
      panel !== null && panel.scrollHeight > 0 ? Math.ceil(panel.scrollHeight) : null;
    // Prefer scrollWidth so overflow past the soft max-width (wide tables) expands the shell.
    // Fall back to the border-box width when scrollWidth is not yet laid out.
    const panelRect = panel?.getBoundingClientRect();
    const scrollWidth = panel != null && panel.scrollWidth > 0 ? Math.ceil(panel.scrollWidth) : null;
    const borderWidth = panelRect != null && panelRect.width > 0 ? Math.ceil(panelRect.width) : null;
    const contentWidth =
      scrollWidth != null && borderWidth != null
        ? Math.max(scrollWidth, borderWidth)
        : (scrollWidth ?? borderWidth);
    const next = computeHoverHintFixedStyle(anchor.getBoundingClientRect(), align, {
      contentHeight,
      contentWidth,
    });
    setPositioned((previous) =>
      mergeMeasuredPortaledHoverHintStyle({
        anchorRevision,
        previous,
        nextStyle: next,
      }),
    );
  }, [align, anchorRevision]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    reposition();
    const frame = requestAnimationFrame(() => {
      reposition();
    });

    const panel = panelRef.current;
    const resizeObserver =
      panel && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            reposition();
          })
        : null;
    if (resizeObserver && panel) {
      resizeObserver.observe(panel);
    }

    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    window.visualViewport?.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("scroll", reposition);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      window.visualViewport?.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("scroll", reposition);
    };
  }, [open, align, reposition, anchorRevision]);

  return resolvePortaledHoverHintStyle({
    open,
    anchorRevision,
    positioned,
    provisionalStyle,
  });
}

function documentBody(): HTMLElement | null {
  return typeof document !== "undefined" ? document.body : null;
}

const HoverHintRootIdContext = createContext<string | null>(null);

function useHoverHintTreeIds(): { rootId: string } {
  const instanceId = useId();
  const inheritedRoot = useContext(HoverHintRootIdContext);
  return { rootId: inheritedRoot ?? instanceId };
}

function clientRectOf(element: HTMLElement | null): HoverHintRect | null {
  if (element == null) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) return null;
  return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
}

function hintPanelRects(rootId: string): HoverHintRect[] {
  if (rootId === "" || typeof document === "undefined") return [];
  const escaped = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(rootId) : rootId;
  const panels = document.querySelectorAll(
    `${HOVER_HINT_PANEL_SELECTOR}[${HOVER_HINT_ROOT_ATTR}="${escaped}"]`,
  );
  const rects: HoverHintRect[] = [];
  panels.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const rect = clientRectOf(node);
    if (rect != null) rects.push(rect);
  });
  return rects;
}

/**
 * When a dialog or other overlay covers the anchor, `pointerleave` on the anchor often does not run, so hover state
 * can stick open while the portaled panel (high z-index) stays visible.
 *
 * Panels are interactive. Leaving both the trigger and the panel closes on the same pointer event when the pointer
 * is not heading toward the other one. Movement toward that box (the safe triangle across the gap) keeps the hint
 * open; stopping in the gap, reversing, or heading away closes. A different hover hint, click-away, and Escape close
 * immediately. Keyboard focus leaving the surface closes as well.
 */
function useDismissPortaledHoverHintOnOutsideInteraction(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  close: () => void,
  rootId: string,
  openingGuardRef?: RefObject<{ openedAtMs: number; pointerId: number | null } | null>,
  /** When false, pointer travel does not dismiss (a click-pinned hint). Click-away and Escape still do. */
  pointerMoveDismissRef?: RefObject<boolean>,
  /** Fired when the pointer is outside but still heading toward the trigger or the panel. */
  onApproach?: () => void,
  /** Controlled hints whose parent clears `open` on leave: arm the stop detector as soon as we listen. */
  armStopOnListen = false,
): void {
  const closeRef = useRef(close);
  closeRef.current = close;
  const onApproachRef = useRef(onApproach);
  onApproachRef.current = onApproach;
  const previousRef = useRef<HoverHintPoint | null>(null);
  const destinationRef = useRef<"panel" | "anchor">("panel");
  const anchorRectRef = useRef<HoverHintRect | null>(null);

  useEffect(() => {
    if (open) return;
    previousRef.current = null;
    destinationRef.current = "panel";
    anchorRectRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let dismissed = false;
    let idleTimer: number | null = null;
    if (anchorRectRef.current == null) anchorRectRef.current = clientRectOf(anchorRef.current);

    const safeClose = () => {
      if (dismissed) {
        return;
      }
      dismissed = true;
      if (idleTimer != null) {
        window.clearTimeout(idleTimer);
        idleTimer = null;
      }
      closeRef.current();
    };

    const clearIdle = () => {
      if (idleTimer != null) {
        window.clearTimeout(idleTimer);
        idleTimer = null;
      }
    };

    const armIdle = () => {
      clearIdle();
      idleTimer = window.setTimeout(() => {
        idleTimer = null;
        safeClose();
      }, HOVER_HINT_APPROACH_IDLE_MS);
    };

    const pointerMoveDismisses = (): boolean => pointerMoveDismissRef?.current !== false;

    const isInsideSurface = (node: EventTarget | null): boolean => {
      return isNodeInsideHoverHintSurface(node, anchorRef.current, rootId);
    };

    const onPointerDownCapture = (event: PointerEvent) => {
      const guard = openingGuardRef?.current;
      if (
        guard != null &&
        shouldIgnoreOpeningPointer({
          openedAtMs: guard.openedAtMs,
          openingPointerId: guard.pointerId,
          nowMs: Date.now(),
          eventPointerId: event.pointerId,
          eventPointerType: event.pointerType,
        })
      ) {
        return;
      }
      if (isInsideSurface(event.target)) {
        return;
      }
      safeClose();
    };
    const onPointerMoveCapture = (event: PointerEvent) => {
      const point = { x: event.clientX, y: event.clientY };
      const liveAnchor = clientRectOf(anchorRef.current);
      if (liveAnchor != null) anchorRectRef.current = liveAnchor;

      if (isInsideSurface(event.target)) {
        clearIdle();
        const inPanel = isNodeInsideThisHintTree(event.target, rootId);
        const inAnchor = isNodeInsideHoverHintAnchor(event.target, anchorRef.current);
        if (inPanel && !inAnchor) destinationRef.current = "anchor";
        else if (inAnchor && !inPanel) destinationRef.current = "panel";
        previousRef.current = point;
        return;
      }
      if (!pointerMoveDismisses()) {
        previousRef.current = point;
        return;
      }
      if (isNodeInsideForeignHoverHint(event.target, anchorRef.current, rootId)) {
        safeClose();
        return;
      }
      const targetRects =
        destinationRef.current === "panel"
          ? hintPanelRects(rootId)
          : anchorRectRef.current != null
            ? [anchorRectRef.current]
            : [];
      const previous = previousRef.current;
      const aim = previous == null ? "still" : hoverHintPointerAim(previous, point, targetRects);
      previousRef.current = point;
      if (aim === "away") {
        safeClose();
        return;
      }
      onApproachRef.current?.();
      armIdle();
    };
    const onPointerOutCapture = (event: PointerEvent) => {
      if (!pointerMoveDismisses()) return;
      if (event.relatedTarget != null) return;
      safeClose();
    };
    const onFocusInCapture = (event: FocusEvent) => {
      if (!pointerMoveDismisses()) return;
      if (isInsideSurface(event.target)) {
        return;
      }
      safeClose();
    };
    const onKeyDownCapture = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        safeClose();
      }
    };

    if (armStopOnListen) armIdle();

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    document.addEventListener("pointermove", onPointerMoveCapture, true);
    document.addEventListener("pointerout", onPointerOutCapture, true);
    document.addEventListener("focusin", onFocusInCapture, true);
    document.addEventListener("keydown", onKeyDownCapture, true);
    return () => {
      clearIdle();
      document.removeEventListener("pointerdown", onPointerDownCapture, true);
      document.removeEventListener("pointermove", onPointerMoveCapture, true);
      document.removeEventListener("pointerout", onPointerOutCapture, true);
      document.removeEventListener("focusin", onFocusInCapture, true);
      document.removeEventListener("keydown", onKeyDownCapture, true);
    };
  }, [open, anchorRef, rootId, openingGuardRef, pointerMoveDismissRef, armStopOnListen]);
}

function useHoverHintOpenState(): {
  open: boolean;
  openFromPointer: () => void;
  setFocusInside: (inside: boolean) => void;
  closePanel: () => void;
} {
  const [hover, setHover] = useState(false);
  const [focusInside, setFocusInside] = useState(false);

  const openFromPointer = useCallback(() => {
    setHover(true);
  }, []);

  const closePanel = useCallback(() => {
    setHover(false);
    setFocusInside(false);
  }, []);

  return {
    open: hover || focusInside,
    openFromPointer,
    setFocusInside,
    closePanel,
  };
}

function HoverHintPortalPanel({
  panelRef,
  panelStyle,
  portalZIndex,
  rootId,
  children,
  onPointerEnter,
  onPointerLeave,
}: {
  panelRef: RefObject<HTMLSpanElement | null>;
  panelStyle: NonNullable<ReturnType<typeof computeHoverHintFixedStyle>>;
  portalZIndex: number;
  rootId: string;
  children: ReactNode;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}): JSX.Element {
  return (
    <span
      ref={panelRef}
      data-hub-hover-hint-panel=""
      data-hub-hover-hint-root={rootId}
      style={portaledHoverHintPanelStyle(panelStyle, portalZIndex)}
      className={portalHoverHintBodyClass}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <span className={portaledHoverHintInnerClass(children)}>{children}</span>
    </span>
  );
}

export type HoverHintTriggerProps = {
  /** Hover hint **without glyph**: explicit anchor. Usually a button with `aria-label`. */
  trigger: ReactNode;
  /** Panel body. Operators can move onto the panel and use links or other controls inside it. */
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  /** Fill anchor width (narrow grid cells) so `truncate` on the trigger works. */
  block?: boolean;
  /** Close the panel when the trigger is pressed or clicked (for example opening a callout from the anchor). */
  closeOnTriggerActivate?: boolean;
  /** Open the panel when its trigger is clicked or tapped. */
  openOnTriggerActivate?: boolean;
  /** Optional override. Hints inside `[data-hub-modal-overlay]` are raised automatically. */
  portalZIndex?: number;
};

/**
 * **Hover hint without glyph** — anchor on the **`trigger`** control. Opens on hover and on `focus-within` the trigger.
 * Portal positioning; see `docs/ui-style-guide.md` § Hover hints.
 */
export function HoverHintTrigger({
  trigger,
  children,
  align = "start",
  className,
  block = false,
  closeOnTriggerActivate = false,
  openOnTriggerActivate = false,
  portalZIndex,
}: HoverHintTriggerProps): JSX.Element {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const openingGuardRef = useRef<{ openedAtMs: number; pointerId: number | null } | null>(null);
  const pinnedRef = useRef(false);
  /** Click focuses the trigger after pointerdown; that focus must not reopen a hint just closed by activate. */
  const closedByActivateRef = useRef(false);
  const [pinned, setPinned] = useState(false);
  const { rootId } = useHoverHintTreeIds();
  const { open: hoverOpen, openFromPointer, setFocusInside, closePanel } = useHoverHintOpenState();
  const open = hoverOpen || pinned;
  const closeHint = useCallback(() => {
    pinnedRef.current = false;
    setPinned(false);
    closePanel();
  }, [closePanel]);
  const pointerMoveDismissRef = useRef(true);
  pointerMoveDismissRef.current = !pinned;
  const closeFromTriggerActivate = useCallback(() => {
    closedByActivateRef.current = true;
    closeHint();
  }, [closeHint]);
  useDismissPortaledHoverHintOnOutsideInteraction(
    wrapRef,
    open,
    closeHint,
    rootId,
    openOnTriggerActivate ? openingGuardRef : undefined,
    pointerMoveDismissRef,
  );
  const panelStyle = usePortaledHoverHintStyle(wrapRef, panelRef, open, align);
  const target = documentBody();

  return (
    <HoverHintRootIdContext.Provider value={rootId}>
    <>
      <span
        ref={wrapRef}
        data-hub-hover-hint-anchor=""
        className={cn(
          "relative",
          block ? "flex min-w-0 w-full flex-col" : "inline-flex items-center",
          className,
        )}
        onPointerEnter={openFromPointer}
        onPointerLeave={() => {
          closedByActivateRef.current = false;
        }}
        onPointerDown={
          openOnTriggerActivate && !closeOnTriggerActivate
            ? (event) => {
                openingGuardRef.current = {
                  openedAtMs: Date.now(),
                  pointerId: event.pointerId,
                };
                pinnedRef.current = true;
                setPinned(true);
                openFromPointer();
              }
            : undefined
        }
        onFocusCapture={() => {
          if (closedByActivateRef.current) {
            return;
          }
          setFocusInside(true);
        }}
        onClick={
          openOnTriggerActivate || closeOnTriggerActivate
            ? (event) => {
                if (closeOnTriggerActivate) {
                  closeFromTriggerActivate();
                }
                if (openOnTriggerActivate) {
                  event.stopPropagation();
                  if (!closeOnTriggerActivate) {
                    setFocusInside(true);
                  }
                }
              }
            : undefined
        }
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocusInside(false);
          }
        }}
      >
        <span
          className={cn(block ? "block min-w-0 w-full overflow-hidden" : "inline-flex")}
          onPointerDown={
            closeOnTriggerActivate
              ? () => {
                  closeFromTriggerActivate();
                }
              : undefined
          }
          onClick={
            closeOnTriggerActivate
              ? () => {
                  closeFromTriggerActivate();
                }
              : undefined
          }
        >
          {trigger}
        </span>
      </span>
      {open && panelStyle != null && target
        ? createPortal(
            <HoverHintPortalPanel
              panelRef={panelRef}
              panelStyle={panelStyle}
              portalZIndex={resolveHoverHintPortalZIndex(wrapRef.current, portalZIndex)}
              rootId={rootId}
              onPointerEnter={openFromPointer}
            >
              {children}
            </HoverHintPortalPanel>,
            target,
          )
        : null}
    </>
    </HoverHintRootIdContext.Provider>
  );
}

export type PortaledHoverHintProps = {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  align?: "start" | "center" | "end";
  children: ReactNode;
  /** Increment when the anchor element changes while `open` stays true (shared grid tooltips). */
  anchorRevision?: number;
};

/**
 * Single portaled hover-hint panel anchored to an external element. Use when many hit targets share one tooltip
 * (for example forecast grid cells) instead of mounting {@link HoverHintContent} per cell.
 */
export function PortaledHoverHint({
  open,
  anchorRef,
  align = "start",
  children,
  anchorRevision = 0,
}: PortaledHoverHintProps): JSX.Element | null {
  const { rootId } = useHoverHintTreeIds();
  const panelRef = useRef<HTMLSpanElement>(null);
  const [panelHover, setPanelHover] = useState(false);
  const [approachHold, setApproachHold] = useState(false);
  const previousOpenRef = useRef(open);
  const releasedRef = useRef(false);
  const closingBridge = previousOpenRef.current && !open && !releasedRef.current;
  const heldChildrenRef = useRef(children);
  if (open && children != null) heldChildrenRef.current = children;
  const show = open || panelHover || approachHold || closingBridge;
  const releaseHold = useCallback(() => {
    releasedRef.current = true;
    setPanelHover(false);
    setApproachHold(false);
  }, []);
  useDismissPortaledHoverHintOnOutsideInteraction(
    anchorRef,
    show,
    releaseHold,
    rootId,
    undefined,
    undefined,
    () => setApproachHold(true),
    !open && show,
  );
  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = open;
    if (open) {
      releasedRef.current = false;
      setApproachHold(false);
      return;
    }
    if (wasOpen && !releasedRef.current) setApproachHold(true);
  }, [open]);

  const panelStyle = usePortaledHoverHintStyle(anchorRef, panelRef, show, align, anchorRevision);
  const target = documentBody();

  if (!show || panelStyle == null || !target) {
    return null;
  }

  return createPortal(
    <HoverHintRootIdContext.Provider value={rootId}>
    <HoverHintPortalPanel
      panelRef={panelRef}
      panelStyle={panelStyle}
      portalZIndex={resolveHoverHintPortalZIndex(anchorRef.current)}
      rootId={rootId}
      onPointerEnter={() => {
        setApproachHold(false);
        setPanelHover(true);
      }}
    >
      {open ? children : heldChildrenRef.current}
    </HoverHintPortalPanel>
    </HoverHintRootIdContext.Provider>,
    target,
  );
}

export type HoverHintContentProps = {
  /** Portal body: plain text or structured content (hover hint without glyph, content-anchored). */
  hoverHint: ReactNode;
  /** Becomes the hover/focus target (no separate ? icon). */
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  /** Fill width (table cells, truncated rows). */
  block?: boolean;
  /**
   * `help` shows the question-mark cursor on the wrapper. Use `default` for large hit targets (for example a full card)
   * so content reads as normal UI instead of a help surface.
   */
  cursor?: "help" | "default";
  /** Optional override. Hints inside `[data-hub-modal-overlay]` are raised automatically. */
  portalZIndex?: number;
  /**
   * On touch, the first tap on a nested link opens the panel instead of navigating; a second tap follows the link.
   * Use for {@link MrpSkuHoverPeek} and similar peek-through links on floor tablets.
   */
  openOnPointerActivate?: boolean;
};

/**
 * **Hover hint without glyph** — anchor on **wrapped children**; body is **`hoverHint`** only. Wrapper is `tabIndex={0}` for keyboard.
 * Prefer {@link HoverHintTrigger} when children include their own strongly focusable controls (avoid double tab stops).
 */
export function HoverHintContent({
  hoverHint,
  children,
  align = "start",
  className,
  block,
  cursor = "help",
  portalZIndex,
  openOnPointerActivate = false,
}: HoverHintContentProps): JSX.Element {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const { rootId } = useHoverHintTreeIds();
  const { open, openFromPointer, setFocusInside, closePanel } = useHoverHintOpenState();
  useDismissPortaledHoverHintOnOutsideInteraction(wrapRef, open, closePanel, rootId);
  const panelStyle = usePortaledHoverHintStyle(wrapRef, panelRef, open, align);
  const target = documentBody();

  return (
    <HoverHintRootIdContext.Provider value={rootId}>
    <>
      <span
        ref={wrapRef}
        data-hub-hover-hint-anchor=""
        className={cn(
          "relative outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 rounded-sm",
          cursor === "help" ? "cursor-help" : "cursor-default",
          block ? "flex min-w-0 flex-1 flex-col" : "inline-flex max-w-full items-center",
          className,
        )}
        tabIndex={openOnPointerActivate ? -1 : 0}
        onPointerEnter={openFromPointer}
        onPointerDownCapture={
          openOnPointerActivate
            ? (event) => {
                if (event.button !== 0) return;
                const link = (event.target as HTMLElement | null)?.closest("a[href]");
                if (link == null || !event.currentTarget.contains(link)) return;
                if (!open) {
                  event.preventDefault();
                  openFromPointer();
                  setFocusInside(true);
                }
              }
            : undefined
        }
        onFocus={() => setFocusInside(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocusInside(false);
          }
        }}
      >
        <span className={cn(block ? "min-w-0" : undefined, block && "flex min-w-0 flex-1 flex-col")}>{children}</span>
      </span>
      {open && panelStyle != null && target
        ? createPortal(
            <HoverHintPortalPanel
              panelRef={panelRef}
              panelStyle={panelStyle}
              portalZIndex={resolveHoverHintPortalZIndex(wrapRef.current, portalZIndex)}
              rootId={rootId}
              onPointerEnter={openFromPointer}
            >
              {hoverHint}
            </HoverHintPortalPanel>,
            target,
          )
        : null}
    </>
    </HoverHintRootIdContext.Provider>
  );
}

export type ReadOnlyHoverLayerProps = {
  /** When true, children are wrapped so a read-only message appears on hover (works with disabled controls). */
  active: boolean;
  hoverHint: string;
  children: ReactNode;
  className?: string;
  /** `inline`: icon buttons and pills. `block`: full-width sections (forms, fieldsets). */
  layout?: "inline" | "block";
};

/**
 * Replaces `title={readOnly ? hoverHint : undefined}` on wrappers around disabled hub controls. Uses a transparent
 * overlay so hover works even when a native `disabled` button would swallow pointer events.
 */
export function ReadOnlyHoverLayer({
  active,
  hoverHint,
  children,
  className,
  layout = "inline",
}: ReadOnlyHoverLayerProps): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const { rootId } = useHoverHintTreeIds();
  const [hover, setHover] = useState(false);
  const openFromPointer = useCallback(() => {
    setHover(true);
  }, []);
  const portalOpen = active && hover;
  const closePanel = useCallback(() => {
    setHover(false);
  }, []);
  useDismissPortaledHoverHintOnOutsideInteraction(wrapRef, portalOpen, closePanel, rootId);
  const panelStyle = usePortaledHoverHintStyle(wrapRef, panelRef, portalOpen, "start");
  const target = documentBody();

  useEffect(() => {
    if (!active) {
      setHover(false);
    }
  }, [active]);

  if (!active) {
    return <>{children}</>;
  }
  const outer =
    layout === "block"
      ? "group/readonly relative flex w-full min-w-0 flex-col"
      : "group/readonly relative inline-flex max-w-full";
  const inner = layout === "block" ? "relative z-0 flex min-w-0 flex-1 flex-col" : "relative z-0 inline-flex max-w-full";

  return (
    <HoverHintRootIdContext.Provider value={rootId}>
    <>
      <div
        ref={wrapRef}
        data-hub-hover-hint-anchor=""
        className={cn(outer, className)}
        onPointerEnter={openFromPointer}
      >
        <span
          className="pointer-events-auto absolute inset-0 z-[1] cursor-not-allowed rounded-[inherit]"
          aria-hidden
        />
        <div className={inner}>{children}</div>
      </div>
      {portalOpen && panelStyle != null && target
        ? createPortal(
            <HoverHintPortalPanel
              panelRef={panelRef}
              panelStyle={panelStyle}
              portalZIndex={resolveHoverHintPortalZIndex(wrapRef.current)}
              rootId={rootId}
              onPointerEnter={openFromPointer}
            >
              {hoverHint}
            </HoverHintPortalPanel>,
            target,
          )
        : null}
    </>
    </HoverHintRootIdContext.Provider>
  );
}

export type HoverHintWithGlyphProps =
  | {
      /** Plain text for the panel and assistive label on the help button. */
      hoverHint: string;
      className?: string;
      size?: "sm" | "md";
      align?: "start" | "center" | "end";
      portalZIndex?: number;
      hoverHintContent?: never;
      hoverHintAriaLabel?: never;
    }
  | {
      /** Structured hover body; use `hoverHintAriaLabel` for the help button `aria-label`. */
      hoverHintContent: ReactNode;
      hoverHintAriaLabel: string;
      className?: string;
      size?: "sm" | "md";
      align?: "start" | "center" | "end";
      portalZIndex?: number;
      hoverHint?: never;
    };

/**
 * **Hover hint with glyph** — fixed `HelpCircle` button beside titles/labels. Built on {@link HoverHintTrigger}.
 * `tabIndex={-1}` so **Tab** skips the glyph; hover/focus-within opens the panel. See `docs/ui-style-guide.md` § Hover hints + Section titles.
 */
export function HoverHintWithGlyph(props: HoverHintWithGlyphProps): JSX.Element {
  const { className, size = "sm", align, portalZIndex } = props;
  const alignResolved = align ?? (size === "md" ? "center" : "start");
  if ("hoverHintContent" in props) {
    return (
      <HoverHintTrigger
        align={alignResolved}
        portalZIndex={portalZIndex}
        openOnTriggerActivate
        trigger={
          <button
            type="button"
            tabIndex={-1}
            className={cn(helpButtonClass, className)}
            aria-label={props.hoverHintAriaLabel}
          >
            <HelpCircle className={cn(helpIconClass[size], "shrink-0")} aria-hidden />
          </button>
        }
      >
        {props.hoverHintContent}
      </HoverHintTrigger>
    );
  }
  return (
    <HoverHintTrigger
      align={alignResolved}
      portalZIndex={portalZIndex}
      openOnTriggerActivate
      trigger={
        <button type="button" tabIndex={-1} className={cn(helpButtonClass, className)} aria-label={props.hoverHint}>
          <HelpCircle className={cn(helpIconClass[size], "shrink-0")} aria-hidden />
        </button>
      }
    >
      {props.hoverHint}
    </HoverHintTrigger>
  );
}

export function CardTitleWithHelp({
  children,
  hoverHint,
  className,
  titleClassName,
}: PropsWithChildren<{ hoverHint: string; className?: string; titleClassName?: string }>): JSX.Element {
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      <CardTitle className={cn("mb-0", titleClassName)}>{children}</CardTitle>
      <HoverHintWithGlyph hoverHint={hoverHint} />
    </div>
  );
}

export type PageTitleWithHelpProps = PropsWithChildren<{
  breadcrumbLabel?: string;
  className?: string;
  titleClassName?: string;
}> &
  (
    | { hoverHint: string; hoverHintContent?: never; hoverHintAriaLabel?: never }
    | { hoverHintContent: ReactNode; hoverHintAriaLabel: string; hoverHint?: never }
  );

export function PageTitleWithHelp(props: PageTitleWithHelpProps): JSX.Element {
  const { children, breadcrumbLabel, className, titleClassName } = props;
  const { trail } = useNavigationTrail();
  usePageBreadcrumb(resolvePageBreadcrumbLabel(breadcrumbLabel, children));

  let helpGlyph: JSX.Element;
  if (typeof props.hoverHint === "string") {
    helpGlyph = <HoverHintWithGlyph hoverHint={props.hoverHint} size="md" />;
  } else {
    helpGlyph = (
      <HoverHintWithGlyph
        hoverHintContent={props.hoverHintContent}
        hoverHintAriaLabel={props.hoverHintAriaLabel}
        size="md"
      />
    );
  }

  if (trail.length > 0) {
    return (
      <HubBreadcrumbTitleRow title={children} className={className} helpGlyph={helpGlyph} />
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <h1 className={cn("text-xl font-semibold text-slate-900", titleClassName)}>{children}</h1>
      {helpGlyph}
    </div>
  );
}
